import type { Booking, BookingStatus, OfficeTimestamp, Usage, UsageStatus } from './types';

// Pure presentation logic (no React, no fetch) — unit-tested in format.test.ts. Mirrors the
// internal admin's bookings/usage helpers.

// --- bookings ---

export function ts_to_date(t: OfficeTimestamp | string | null | undefined): Date | null {
  if (!t) return null;
  if (typeof t === 'string') {
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof t._seconds === 'number') return new Date(t._seconds * 1000);
  return null;
}

export function fmt_dt(t: OfficeTimestamp | string | null | undefined): string {
  const d = ts_to_date(t);
  return d ? d.toLocaleString() : '—';
}

export function status_color(s: BookingStatus | undefined): string {
  switch (s) {
    case 'BOOKED':
    case 'CONFIRMED':
      return 'var(--green-700)';
    case 'PENDING':
    case 'PENDING_NO_PHONE':
    case 'RESCHEDULED':
      return '#b58900';
    case 'EXPIRED':
    case 'CANCELLED':
      return '#b3261e';
    default:
      return 'var(--muted)';
  }
}

// A booking's best-known contact line (contact-prominent detail).
export function booking_contact(b: Booking): string {
  return b.client_phone || b.prospect_email || (b.client_email_only ? 'email only' : '—');
}

// --- usage ---

export function pct(used: number, limit: number): number {
  if (!limit || limit <= 0) return 0;
  return (used / limit) * 100;
}
export const minutes_pct = (u: Usage): number => pct(u.used.minutes, u.limits.minutes);
export const messages_pct = (u: Usage): number => pct(u.used.messages, u.limits.messages);
export const consumed_pct = (u: Usage): number => Math.max(minutes_pct(u), messages_pct(u));

export function status_of(u: Usage): UsageStatus {
  if (u.over_minutes || u.over_messages || consumed_pct(u) >= 100) return 'over';
  if (consumed_pct(u) >= 80) return 'warn';
  return 'ok';
}

export function usage_status_color(s: UsageStatus): string {
  switch (s) {
    case 'over':
      return '#b3261e';
    case 'warn':
      return '#b58900';
    default:
      return 'var(--green-700)';
  }
}

export function days_remaining(cycle_end_iso: string, now: Date = new Date()): number {
  const end = new Date(cycle_end_iso).getTime();
  if (Number.isNaN(end)) return 0;
  const ms = end - now.getTime();
  return ms <= 0 ? 0 : Math.ceil(ms / 86_400_000);
}

export function fmt_pacific_date(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

export const fmt_cycle_window = (c: { start: string; end: string }): string =>
  `${fmt_pacific_date(c.start)} → ${fmt_pacific_date(c.end)} PT`;

export const fmt_pct = (n: number): string => `${Math.round(n)}%`;
